import { useRef, useState } from 'react';
import { Separator } from '@monksflow/monks-ui';

import { BrandLogo } from '@/shared/ui/BrandLogo/BrandLogo';

import type { CompetitorDto } from '@inference.engine/api-client';
import type { StatistaDataset } from '@/entities/analysis';
import { StaggerItem } from '@/shared/ui/animations';
import { AnalysisSubHeader, BrandTabs } from '../../../../components';
import { StatistaChartCard } from '../StatistaCharts';
import { findCitedStatistaDatasets } from '../../lib/statistaCitations';
import { CompetitorDetail } from './CompetitorDetail';
import { useTranslation } from 'react-i18next';

interface CompetitiveAnalysisProps {
  topCompetitors?: CompetitorDto[];
  brandName?: string;
  /**
   * Export (print page) mode. The live view is a selector — a tab strip picks
   * one competitor's detail — which can't be driven in a static PDF, so on
   * export we drop the selector and render every competitor's detail.
   */
  exporting?: boolean;
  /** Every Statista dataset the analysis holds; the block matches the competitor on screen. */
  datasets?: StatistaDataset[];
}

export const CompetitiveAnalysis = ({
  topCompetitors,
  brandName,
  exporting,
  datasets,
}: CompetitiveAnalysisProps) => {
  const { t } = useTranslation('analysis');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  if (!topCompetitors?.length) return null;

  const selectedCompetitor = topCompetitors[selectedIndex];
  // Each competitor's charts sit with that competitor: beside its detail on the export,
  // and following the selector in the live view.
  const chartsFor = (competitor: CompetitorDto) =>
    findCitedStatistaDatasets(datasets ?? [], competitor);

  const handleSelect = (value: string) => {
    setSelectedIndex(Number(value));
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={sectionRef} className="flex flex-col gap-10">
      <AnalysisSubHeader
        title={t('analysisResult.competitiveAnalysis.competitiveAnalysis')}
        subtitle={t('analysisResult.competitiveAnalysis.marketSharesAndRevenueFigures')}
        subtitleTone="meta"
      />

      {exporting ? (
        // Every competitor, in order — no selector — with a divider between brands.
        topCompetitors.map((competitor, index) => (
          <StaggerItem key={index} index={index}>
            {index > 0 && <Separator className="mb-6" />}
            {/* A PDF has no tab strip to say which competitor this is, so the export
                names each block itself. */}
            <div className="mb-4 flex flex-row items-center gap-3">
              <BrandLogo brandName={competitor.name ?? ''} website={competitor.website} size={40} />
              <span className="text-heading-lg font-medium text-foreground">{competitor.name}</span>
            </div>
            <CompetitorDetail competitor={competitor} brandName={brandName} />
            {chartsFor(competitor).map((cited) => (
              <StatistaChartCard key={cited.id} dataset={cited} citedIn={competitor} />
            ))}
          </StaggerItem>
        ))
      ) : (
        <>
          <BrandTabs
            ariaLabel={t('analysisResult.competitiveAnalysis.competitors')}
            value={String(selectedIndex)}
            onValueChange={handleSelect}
            tabs={topCompetitors.map((competitor, index) => ({
              value: String(index),
              label: competitor.name ?? '',
              icon: (
                <BrandLogo
                  brandName={competitor.name ?? ''}
                  website={competitor.website}
                  size={20}
                />
              ),
            }))}
          />

          <CompetitorDetail competitor={selectedCompetitor} brandName={brandName} />
          {chartsFor(selectedCompetitor).map((cited) => (
            <StatistaChartCard key={cited.id} dataset={cited} citedIn={selectedCompetitor} />
          ))}
        </>
      )}
    </div>
  );
};
